/* ── Projector Card Nav ─────────────────────────────────────────────────────
   The projector dropdown at the LEFT of the /profile floating nav, given the
   reactbits Card Nav treatment (gsap staggered card reveal).

   The card stack is a PROJECTOR SWITCHER: the trigger shows the projector being
   viewed; opening reveals the other projector profiles as cards (switch on
   click), plus a compact footer of nav actions (All Projectors / Dashboard /
   Account / Log Out). Closes on outside-click / Escape.

   The backdrop AND the panel are both portaled to <body> so the panel stays
   above the blurred backdrop (the header's own stacking context otherwise put
   the backdrop's blur on top of the panel). The panel is positioned fixed just
   below the trigger.

   Props:
     currentName, currentImage        — collapsed trigger content
     profiles: [{ id, name, image, isCurrent }]
     onSelectProfile(profile)         — switch to that projector
     footerLinks: [{ label, onClick }]
     ease                             — gsap ease (default 'power3.out') */
import {
  useRef, useState, useLayoutEffect, useEffect, useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import { GoArrowUpRight } from 'react-icons/go';
import './ProjectorCardNav.css';

export default function ProjectorCardNav({
  currentName,
  currentImage,
  profiles = [],
  onSelectProfile,
  footerLinks = [],
  ease = 'power3.out',
}) {
  const [open, setOpen] = useState(false);
  const [hoveredFooter, setHoveredFooter] = useState(-1);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const cardsRef = useRef([]);
  const tlRef = useRef(null);

  const setCardRef = (i) => (el) => {
    if (el) cardsRef.current[i] = el;
  };

  // Anchor the fixed, body-portaled panel just below the trigger.
  const positionPanel = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    setPos({ top: r.bottom + 12, left: r.left });
  }, []);

  const toggle = () => {
    const next = !open;
    if (next) positionPanel();
    setOpen(next);
  };

  const buildTimeline = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return null;
    const cards = cardsRef.current.filter(Boolean);
    gsap.set(panel, { autoAlpha: 0, y: -8 });
    gsap.set(cards, { y: 22, opacity: 0 });
    const tl = gsap.timeline({ paused: true });
    tl.to(panel, { autoAlpha: 1, y: 0, duration: 0.26, ease });
    tl.to(cards, {
      y: 0, opacity: 1, duration: 0.3, ease, stagger: 0.05,
    }, '-=0.12');
    return tl;
  }, [ease]);

  useLayoutEffect(() => {
    const tl = buildTimeline();
    tlRef.current = tl;
    return () => {
      if (tl) tl.kill();
      tlRef.current = null;
    };
  }, [buildTimeline, profiles.length]);

  useEffect(() => {
    const tl = tlRef.current;
    if (!tl) return;
    if (open) tl.play();
    else tl.reverse();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const inTrigger = rootRef.current && rootRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const reposition = () => positionPanel();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', reposition);
    };
  }, [open, positionPanel]);

  return (
    <>
      <div className={`projector-cardnav ${open ? 'open' : ''}`} ref={rootRef}>
        <button
          type="button"
          ref={triggerRef}
          className="pcn-trigger"
          onClick={toggle}
          aria-expanded={open}
          aria-haspopup="true"
        >
          {currentImage ? (
            <img className="pcn-avatar" src={currentImage} alt="" />
          ) : (
            <span className="pcn-avatar pcn-avatar-fallback">
              {(currentName || 'P').trim().charAt(0).toUpperCase()}
            </span>
          )}
          {currentName ? (
            <span className="pcn-name">{currentName}</span>
          ) : (
            <span className="pcn-name-shimmer" aria-hidden="true" />
          )}
          <svg
            className="pcn-chevron"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </div>

      {createPortal(
        <>
          {open && (
            <div
              className="pcn-backdrop"
              aria-hidden="true"
              onClick={() => setOpen(false)}
            />
          )}
          <div
            className="pcn-panel pcn-panel-portaled"
            ref={panelRef}
            style={{ top: pos.top, left: pos.left }}
            aria-hidden={!open}
          >
            <div className="pcn-panel-head">
              <span className="pcn-panel-title">Switch projector</span>
            </div>
            <div className="pcn-profiles">
              {profiles.length ? (
                profiles.map((p, i) => (
                  <button
                    type="button"
                    key={p.id ?? i}
                    ref={setCardRef(i)}
                    className={`pcn-profile-card ${p.isCurrent ? 'current' : ''}`}
                    onClick={() => { setOpen(false); if (onSelectProfile) onSelectProfile(p); }}
                  >
                    {p.image ? (
                      <img className="pcn-profile-img" src={p.image} alt="" />
                    ) : (
                      <span className="pcn-profile-img pcn-profile-fallback">
                        {(p.name || 'P').trim().charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="pcn-profile-name">{p.name}</span>
                    {p.isCurrent ? <span className="pcn-current-dot" aria-label="current" /> : null}
                  </button>
                ))
              ) : (
                <div className="pcn-empty">No other projectors</div>
              )}
            </div>
            {footerLinks.length ? (
              <div className="pcn-footer">
                {footerLinks.map((l, i) => {
                  const FooterIcon = l.Icon;
                  return (
                    <button
                      type="button"
                      key={`${l.label}-${i}`}
                      className="pcn-footer-link"
                      onMouseEnter={() => setHoveredFooter(i)}
                      onMouseLeave={() => setHoveredFooter(-1)}
                      onClick={() => { setOpen(false); if (l.onClick) l.onClick(); }}
                    >
                      <span className="pcn-footer-icon" aria-hidden="true">
                        {FooterIcon ? (
                          <FooterIcon size={17} animate={hoveredFooter === i} />
                        ) : (
                          <GoArrowUpRight />
                        )}
                      </span>
                      {l.label}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
